<template>
  <div class="project-details">
    <!-- Loading State -->
    <div v-if="isLoading" class="project-details__loading">
      <div class="project-details__loading-spinner">
        <svg class="project-details__loading-icon animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p>Loading project details...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="project-details__error">
      <p class="project-details__error-message">{{ error }}</p>
      <themed-button @click="fetchProjectData" variant="default">
        Retry
      </themed-button>
    </div>

    <!-- Main Content -->
    <div v-else class="project-details__container">
      <!-- Project Header - Full Width at Top -->
      <detail-page-header
        :title="project.name"
        :show-back-button="true"
        :badges="headerBadges"
        back-button-label="Go back to projects list"
        color-variant="green"
        @back="navigateBack"
      >
        <template #actions>
          <themed-dropdown 
            v-if="canManageProject"
            label="Manage Project"
            :actions="manageProjectActions"
            @action="handleManageProjectAction"
          />
        </template>
      </detail-page-header>

      <!-- ARIA Live Region for announcements -->
      <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
        <span v-if="announcement">{{ announcement }}</span>
      </div>

      <!-- Content Grid: Left Column + Right Tabs -->
      <div class="project-details__content-grid">
        <!-- Left Column: Benchmark + Details + KSDs -->
        <div class="project-details__left-column">
          <!-- Benchmark Gauge -->
          <benchmark-gauge
            title="Project Benchmark to Market"
            height="250"
            :benchmark="project.benchmark"
          ></benchmark-gauge>

          <!-- Project Details Panel -->
        <div class="project-details-panel">
          <div class="project-details-panel__header">
            <h2 class="project-details-panel__title">Project Details</h2>
            <themed-button
              v-if="canEditProject && !isEditingDetails"
              variant="ghost"
              size="icon"
              @click="startEditingDetails"
              class="project-details-panel__edit-button"
              aria-label="Edit project details"
            >
              <theme-icon icon="edit" type="icons" classes="w-4 h-4" />
            </themed-button>
          </div>

          <div class="project-details-panel__content">
            <div class="project-details-panel__field">
              <span class="project-details-panel__label">Name</span>
              <span v-if="!isEditingDetails" class="project-details-panel__value">{{ project.name }}</span>
              <themed-input
                v-if="isEditingDetails"
                v-model="project.name"
                type="text"
                class="project-details-panel__input"
              />
            </div>
            <div class="project-details-panel__field">
              <span class="project-details-panel__label">Start Date</span>
              <span v-if="!isEditingDetails" class="project-details-panel__value">{{ formatDate(project.start_date) }}</span>
              <themed-input
                v-if="isEditingDetails"
                v-model="project.start_date"
                type="date"
                class="project-details-panel__input"
              />
            </div>
            <div class="project-details-panel__field">
              <span class="project-details-panel__label">End Date</span>
              <span v-if="!isEditingDetails" class="project-details-panel__value">{{ formatDate(project.end_date) }}</span>
              <themed-input
                v-if="isEditingDetails"
                v-model="project.end_date"
                type="date"
                class="project-details-panel__input"
              />
            </div>
            <div class="project-details-panel__field">
              <span class="project-details-panel__label">Usage</span>
              <span v-if="!isEditingDetails" class="project-details-panel__value">{{ project.property_usage || 'N/A' }}</span>
              <select
                v-if="isEditingDetails"
                v-model="project.property_usage"
                class="project-details-panel__select"
              >
                <option value="">Select usage</option>
                <option value="Canadian Ops">Canadian Ops</option>
                <option value="Corporate HQ">Corporate HQ</option>
                <option value="Digital Retailing">Digital Retailing</option>
                <option value="DMS">DMS</option>
                <option value="Inventory/Interactive">Inventory/Interactive</option>
                <option value="Operations">Operations</option>
                <option value="Production">Production</option>
                <option value="RTS">RTS</option>
                <option value="Sales & F&I">Sales & F&I</option>
                <option value="Sales RTS">Sales RTS</option>
                <option value="Sublet">Sublet</option>
                <option value="Technology RTS">Technology RTS</option>
                <option value="Vacant">Vacant</option>
              </select>
            </div>
            <div class="project-details-panel__field">
              <span class="project-details-panel__label">Region</span>
              <span v-if="!isEditingDetails" class="project-details-panel__value">{{ project.property_region || 'N/A' }}</span>
              <select
                v-if="isEditingDetails"
                v-model="project.property_region"
                class="project-details-panel__select"
              >
                <option value="">Select region</option>
                <option value="Central">Central</option>
                <option value="Mid-Atlantic">Mid-Atlantic</option>
                <option value="Northeast">Northeast</option>
                <option value="Northwest">Northwest</option>
                <option value="Outside USA">Outside USA</option>
                <option value="Southeast">Southeast</option>
                <option value="Southwest">Southwest</option>
              </select>
            </div>
            <div class="project-details-panel__field">
              <span class="project-details-panel__label">Current Sq. Ft</span>
              <span class="project-details-panel__value">{{ formatCompactNumber(project.current_sq_ft) }}</span>
            </div>
            <div class="project-details-panel__field">
              <span class="project-details-panel__label">Required Sq. Ft</span>
              <span v-if="!isEditingDetails" class="project-details-panel__value">{{ formatCompactNumber(project.required_sq_ft) }}</span>
              <themed-input
                v-if="isEditingDetails"
                v-model.number="project.required_sq_ft"
                type="number"
                class="project-details-panel__input"
              />
            </div>
            <div class="project-details-panel__field">
              <span class="project-details-panel__label">Property Type</span>
              <span v-if="!isEditingDetails" class="project-details-panel__value">{{ project.property_type || 'N/A' }}</span>
              <select
                v-if="isEditingDetails"
                v-model="project.property_type"
                class="project-details-panel__select"
              >
                <option value="">Select type</option>
                <option value="Office">Office</option>
                <option value="Industrial">Industrial</option>
                <option value="Flex">Flex</option>
                <option value="Land">Land</option>
                <option value="Retail">Retail</option>
              </select>
            </div>
            <div class="project-details-panel__field">
              <span class="project-details-panel__label">Project Type</span>
              <span v-if="!isEditingDetails" class="project-details-panel__value">{{ project.project_type || 'N/A' }}</span>
              <select
                v-if="isEditingDetails"
                v-model="project.project_type"
                class="project-details-panel__select"
              >
                <option value="">Select type</option>
                <option value="Consolidation">Consolidation</option>
                <option value="Expansion">Expansion</option>
                <option value="New Location">New Location</option>
                <option value="Renewal">Renewal</option>
              </select>
            </div>
            <div class="project-details-panel__field">
              <span class="project-details-panel__label">Stage</span>
              <span v-if="!isEditingDetails" class="project-details-panel__value">{{ project.stage || 'N/A' }}</span>
              <select
                v-if="isEditingDetails"
                v-model="project.stage"
                class="project-details-panel__select"
              >
                <option value="">Select stage</option>
                <option value="Activated Assignment">Activated Assignment</option>
                <option value="Approval">Approval</option>
                <option value="Build-out">Build-out</option>
                <option value="Comps Research">Comps Research</option>
                <option value="Final Cut">Final Cut</option>
                <option value="Lease Negotiation">Lease Negotiation</option>
                <option value="Move In">Move In</option>
                <option value="Negotiation">Negotiation</option>
                <option value="RFP Process">RFP Process</option>
                <option value="Sign Off">Sign Off</option>
                <option value="Tour of Comps">Tour of Comps</option>
              </select>
            </div>
          </div>

          <!-- Edit Actions -->
          <div v-if="isEditingDetails" class="project-details-panel__actions">
            <themed-button
              @click="cancelEditingDetails"
              variant="outline"
              size="sm"
              :disabled="isSaving"
            >
              Cancel
            </themed-button>
            <themed-button
              @click="saveProjectDetails"
              variant="default"
              size="sm"
              :disabled="isSaving"
            >
              <span v-if="isSaving">Saving...</span>
              <span v-else>Save</span>
            </themed-button>
          </div>
        </div>

          <!-- KSD Panel (Left) -->
        <div class="ksd-panel">
          <div class="ksd-panel__header">
            <div class="ksd-panel__header-top">
              <h3 class="ksd-panel__title">KSDs</h3>
              <div class="ksd-panel__header-actions">
                <themed-button
                  v-if="canEditProject && !isEditingKsd"
                  variant="ghost"
                  size="icon"
                  @click="startEditingKsd"
                  class="ksd-panel__edit-button"
                  aria-label="Edit KSD criteria"
                >
                  <theme-icon icon="edit" type="icons" classes="w-4 h-4" />
                </themed-button>
                <themed-button
                  v-if="isEditingKsd"
                  variant="default"
                  size="sm"
                  @click="addKsdCriterion"
                  class="ksd-panel__add-button"
                  aria-label="Add KSD criterion"
                >
                  <theme-icon icon="plus-icon" type="icons" classes="w-4 h-4" />
                  <span>Add</span>
                </themed-button>
              </div>
            </div>
            <div class="ksd-panel__header-bottom">
              <div class="ksd-panel__scenario-selector">
                <button 
                  @click="openManageScenarios" 
                  class="ksd-panel__scenario-button"
                  aria-label="Manage scenarios"
                >
                  Scenario
                </button>
                <select 
                  v-model="selectedScenarioId" 
                  @change="handleScenarioChange"
                  class="ksd-panel__scenario-select"
                  aria-label="Select scenario"
                >
                  <option 
                    v-for="scenario in ksdScenarios" 
                    :key="scenario.id" 
                    :value="scenario.id"
                  >
                    {{ scenario.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div v-if="hasKsdCriteria" class="ksd-panel__content">
            <div class="ksd-panel__table">
              <div class="ksd-panel__table-header">
                <span class="ksd-panel__table-header-cell">DRIVER</span>
                <span class="ksd-panel__table-header-cell">WEIGHT (%)</span>
                <span v-if="isEditingKsd" class="ksd-panel__table-header-cell ksd-panel__table-header-cell--actions"></span>
              </div>
              <div v-for="(criterion, index) in ksdCriteria" :key="criterion.id || 'new-' + index" class="ksd-panel__criterion">
                <div class="ksd-panel__criterion-name-wrapper">
                  <span v-if="!isEditingKsd || criterion.driver === 'Price'" class="ksd-panel__criterion-name">{{ criterion.driver }}</span>
                  <themed-input
                    v-if="isEditingKsd && criterion.driver !== 'Price'"
                    v-model="criterion.driver"
                    type="text"
                    class="ksd-panel__criterion-input"
                    placeholder="Driver name"
                  />
                </div>
                <div class="ksd-panel__criterion-weight-wrapper">
                  <span v-if="!isEditingKsd" class="ksd-panel__criterion-weight">{{ criterion.weight }}%</span>
                  <themed-input
                    v-if="isEditingKsd"
                    v-model.number="criterion.weight"
                    type="number"
                    min="0"
                    max="100"
                    step="5"
                    class="ksd-panel__criterion-input ksd-panel__criterion-input--weight"
                  />
                </div>
                <div v-if="isEditingKsd" class="ksd-panel__criterion-actions">
                  <button
                    v-if="criterion.driver !== 'Price'"
                    @click="removeKsdCriterion(index)"
                    class="ksd-panel__delete-button"
                    aria-label="Delete criterion"
                  >
                    <theme-icon icon="trash" type="icons" classes="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <div class="ksd-panel__unallocated" :class="{ 'ksd-panel__unallocated--error': unallocatedPercentage < 0 }">
              <span class="ksd-panel__unallocated-label">UNALLOCATED PERCENTAGE</span>
              <span class="ksd-panel__unallocated-value">{{ unallocatedPercentage }}%</span>
            </div>
          </div>

          <div v-else class="ksd-panel__empty">
            <p>No KSD criteria configured</p>
          </div>

          <!-- Edit Actions -->
          <div v-if="isEditingKsd" class="ksd-panel__actions">
            <themed-button
              @click="cancelEditingKsd"
              variant="outline"
              size="sm"
              :disabled="isSavingKsd"
            >
              Cancel
            </themed-button>
            <themed-button
              @click="saveKsdCriteria"
              variant="default"
              size="sm"
              :disabled="isSavingKsd || unallocatedPercentage < 0"
            >
              <span v-if="isSavingKsd">Saving...</span>
              <span v-else>Save</span>
            </themed-button>
          </div>
        </div>

        </div><!-- end left-column -->

        <!-- Tabs Section (Right) -->
        <div class="project-details__tabs-section">
          <!-- Tab Navigation -->
          <div class="project-details__tabs" role="tablist" aria-label="Project information tabs">
            <button
              v-for="tab in availableTabs"
              :key="tab.slug"
              role="tab"
              :aria-selected="activeTab === tab.slug"
              :aria-controls="'panel-' + tab.slug"
              :id="'tab-' + tab.slug"
              @click="switchTab(tab.slug)"
              class="project-details__tab"
              :class="{ 'project-details__tab--active': activeTab === tab.slug }"
            >
              {{ tab.label }}
              <span v-if="tab.count !== undefined" class="project-details__tab-count">
                {{ tab.count }}
              </span>
            </button>
          </div>

          <!-- Tab Content -->
          <div class="project-details__tab-panels">
            <!-- Comps Tab -->
            <div
              v-show="activeTab === 'comps'"
              role="tabpanel"
              id="panel-comps"
              aria-labelledby="tab-comps"
              class="project-details__tab-content-other"
            >
              <comps-cards
                :comp-data="project.comps || []"
                :project-id="project.id"
                :show-toolbar="true"
                :show-search="true"
                :show-view-toggle="true"
                :show-sort-filter="true"
                :show-display-count="true"
                :show-manage-button="canManageProject"
                :show-remove-button="true"
                :show-title="true"
                title="Comps"
                empty-message="No comps added yet"
                :default-view-mode="'list'"
                :disable-preferences="false"
                @manage-comps="openManageCompsModal"
                @favorite-toggled="handleCompFavoriteToggled"
                @comp-removed="handleCompRemovedFromList"
              />
            </div>

            <!-- Finalist Grid Tab -->
            <div
              v-show="activeTab === 'finalist-grid'"
              role="tabpanel"
              id="panel-finalist-grid"
              aria-labelledby="tab-finalist-grid"
              class="project-details__tab-content"
            >
              <finalist-grid
                :comp-data="alternativeSites"
                :ksd-criteria="ksdCriteria"
                :project-id="project.id"
                :show-remove-button="canManageProject"
                :is-loading="isLoadingSites"
                empty-message="No finalist comps added yet"
                loading-message="Loading finalist comps..."
                @favorite-toggled="handleCompFavoriteToggled"
                @comp-removed="handleFinalistCompRemoved"
              />
            </div>

            <!-- Sites Tab -->
            <div
              v-show="activeTab === 'sites'"
              role="tabpanel"
              id="panel-sites"
              aria-labelledby="tab-sites"
              class="project-details__tab-content-other"

            >
              <!-- Use SiteCards component with all controls managed internally -->
              <site-cards
                v-if="tabLoaded['sites']"
                :site-data="project.sites || []" 
                :show-toolbar="true"
                :show-search="true"
                :show-view-toggle="true"
                :show-sort-filter="true"
                :show-display-count="false"
                :show-new-site-button="canManageProject"
                :show-title="true"
                :show-pagination="true"
                :default-page-size="10"
                :page-size-options="[5, 10, 20]"
                :default-view-mode="'list'"
                :default-sort-by="sitesSortColumn"
                :default-sort-direction="sitesSortDirection"
                :default-show-archived="false"
                :disable-preferences="true"
                title="Sites"
                @create-site="manageSites"
              />
            </div>

            <!-- Tasks Tab -->
            <div
              v-show="activeTab === 'tasks'"
              role="tabpanel"
              id="panel-tasks"
              aria-labelledby="tab-tasks"
              class="project-details__tab-content-other"
            >
              <task-cards
                v-if="tabLoaded['tasks']"
                :project-id="project.id"
                title="Tasks"
                :show-title="true"
                :searchable="false"
                create-button-text="Create Task"
                modal-name="project-task-form-modal"
                @task-saved="onTaskSaved"
                @task-deleted="onTaskDeleted"
              />
            </div>

            <!-- Proposals Tab -->
            <div
              v-show="activeTab === 'proposals'"
              role="tabpanel"
              id="panel-proposals"
              aria-labelledby="tab-proposals"
              class="project-details__tab-content-other"
            >
              <proposal-cards
                v-if="tabLoaded['proposals']"
                :project-id="project.id" 
                :proposals="project.proposals || []"
                :can-create="canEditProject"
                @proposal-created="handleProposalCreated"
              />
            </div>

            <!-- Tours Tab -->
            <div
              v-show="activeTab === 'tours'"
              role="tabpanel"
              id="panel-tours"
              aria-labelledby="tab-tours"
              class="project-details__tab-content-other"
            >
              <tour-manager
                v-if="tabLoaded['tours']"
                :project-id="project.id"
                :available-comps="project.comps || []"
                :can-manage-tours="canEditProject"
              />
            </div>

            <!-- Key Dates Tab -->
            <div
              v-show="activeTab === 'key-dates'"
              role="tabpanel"
              id="panel-key-dates"
              aria-labelledby="tab-key-dates"
              class="project-details__tab-content"
            >
              <project-key-dates-tab
                v-if="tabLoaded['key-dates']"
                :project-id="project.id" 
                :sites="project.sites || []"
              />
            </div>

            <!-- Contacts Tab -->
            <div
              v-show="activeTab === 'contacts'"
              role="tabpanel"
              id="panel-contacts"
              aria-labelledby="tab-contacts"
              class="project-details__tab-content"
            >
              <contacts
                v-if="tabLoaded['contacts']"
                :contact-data="project.contacts"
                attach-to="project"
                :attach-to-id="project.id"
              />
            </div>

            <!-- Documents Tab -->
            <div
              v-show="activeTab === 'documents'"
              role="tabpanel"
              id="panel-documents"
              aria-labelledby="tab-documents"
              class="project-details__tab-content"
            >
              <documents
                v-if="tabLoaded['documents']"
                :document-data="project.documents"
                :documentable-id="project.id"
                documentable-type="App\Project"
              />
            </div>

            <!-- Other Tabs Placeholder -->
            <!-- Changelog Tab -->
            <div
              v-show="activeTab === 'changelog'"
              role="tabpanel"
              id="panel-changelog"
              aria-labelledby="tab-changelog"
              class="project-details__tab-content"
            >
              <changelog v-if="tabLoaded['changelog']" resource-type="project" :resource-id="project.id" />
            </div>

            <!-- Comments Tab -->
            <div
              v-show="activeTab === 'comments'"
              role="tabpanel"
              id="panel-comments"
              aria-labelledby="tab-comments"
              class="project-details__tab-content"
            >
              <comments
                v-if="tabLoaded['comments']"
                :data="project.comments"
                :document-data="project.documents"
                :commentable-id="project.id"
                commentable-type="App\Project"
              />
            </div>

            <!-- Other Tabs -->
            <div
              v-for="tab in otherTabs.filter(t => t.slug !== 'changelog' && t.slug !== 'comments')"
              :key="'panel-' + tab.slug"
              v-show="activeTab === tab.slug"
              role="tabpanel"
              :id="'panel-' + tab.slug"
              :aria-labelledby="'tab-' + tab.slug"
              class="project-details__tab-content"
            >
              <p class="project-details__placeholder">{{ tab.label }} content coming soon.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Manage Sites Modal -->
    <manage-sites-modal
      :project-id="project.id"
      :project-sites="project.sites || []"
      @sites-updated="handleSitesUpdated"
    />

    <!-- Manage Comps Modal -->
    <manage-comps-modal
      :project-id="project.id"
      :comp-data="project.comps || []"
      @comp-added="handleCompAdded"
      @comp-removed="handleCompRemoved"
    />

    <!-- Manage Scenarios Modal -->
    <manage-scenarios-modal
      :ksdable-id="project.id"
      ksdable-type="App\Project"
      :scenarios="ksdScenarios"
      :scenario="ksdScenarios.find(s => s.id === selectedScenarioId) || ksdScenarios[0] || {}"
      @scenarios-updated="handleScenariosUpdated"
    />

    <!-- Notifications -->
    <notifications classes="notification" position="bottom right"></notifications>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';
import ThemedButton from '../ThemedButton.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemeIcon from '../ThemeIcon.vue';
import BenchmarkGauge from '../BenchmarkGauge.vue';
import ManageSitesModal from './ManageSitesModal.vue';
import ManageCompsModal from './ManageCompsModal.vue';
import ManageScenariosModal from './ManageScenariosModal.vue';
import TaskCards from '../tasks/TaskCards.vue';
import ProjectKeyDatesTab from './ProjectKeyDatesTab.vue';
import Contacts from '../contacts/Contacts.vue';
import ProposalCards from '../proposals/ProposalCards.vue';
import Documents from '../documents/Documents.vue';
import Comments from '../comments/Comments.vue';
import Changelog from '../changelog/Changelog.vue';
import SiteCards from '../sites/SiteCards.vue';
import DetailPageHeader from '../DetailPageHeader.vue';
import ThemedDropdown from '../ThemedDropdown.vue';
import CompsCards from '../comps/CompsCards.vue';
import FinalistGrid from '../comps/FinalistGrid.vue';

export default {
  name: 'ProjectDetails',

  components: {
    ThemedButton,
    ThemedInput,
    ThemeIcon,
    BenchmarkGauge,
    ManageSitesModal,
    ManageCompsModal,
    ManageScenariosModal,
    TaskCards,
    ProjectKeyDatesTab,
    Contacts,
    ProposalCards,
    Documents,
    Comments,
    Changelog,
    SiteCards,
    DetailPageHeader,
    ThemedDropdown,
    CompsCards,
    FinalistGrid
  },

  props: {
    showSearch: {
      type: Boolean,
      default: true
    },
    
    showViewToggle: {
      type: Boolean,
      default: true
    },
    projectData: {
      type: Object,
      required: true
    },
    initialTab: {
      type: String,
      default: 'finalist-grid',
      validator: (value) => [
        'comps', 'sites', 'tasks', 'finalist-grid',
        'proposals', 'key-dates', 'documents',
        'contacts', 'comments', 'changelog'
      ].includes(value)
    },
    defaultViewMode: {
      type: String,
      default: 'grid', // 'grid' or 'list'
      validator: (value) => ['grid', 'list'].includes(value)
    },
    showDisplayCount: {
      type: Boolean,
      default: true
    },
  },

  data() {
    return {
      viewMode: this.defaultViewMode,
      displayCountOpen: false,
      project: this.projectData,
      originalProject: null, // Store original project data for cancel
      activeTab: this.initialTab,
      alternativeSites: [],
      ksdCriteria: [],
      ksdScenarios: [],
      selectedScenarioId: null,
      originalKsdCriteria: null, // Store original KSD data for cancel
      showManageDropdown: false,
      isLoading: false,
      isLoadingSites: false,
      isSaving: false,
      isSavingKsd: false,
      isEditingDetails: false,
      isEditingKsd: false,
      error: null,
      announcement: '',
      // Sites view and filter state
      sitesLayout: 'list', // 'list' or 'grid'
      showSitesFilterPanel: false,
      sitesSortColumn: 'address',
      sitesSortDirection: 'asc',
      sitesSearchQuery: '',
      sitesShowFavorited: false,
      sitesAvailableSqFtMin: 0,
      sitesAvailableSqFtMax: 1000000,
      // KAN-173: track which tabs have been activated at least once
      // so we can use v-if for lazy mounting without re-destroying on tab switch
      tabLoaded: {},
      // Reactive task count — updated by TaskCards via @task-saved / @task-deleted events
      taskCount: null,
    };
  },

  computed: {
    ...mapState(['projects', 'projectComps', 'documents', 'contacts', 'ksds']),

    canEditProject() {
      return this.$gate.can('update', 'project', this.project.id);
    },

    canDeleteProject() {
      return this.$gate.can('delete', 'project', this.project.id);
    },

    canManageProject() {
      return this.canEditProject || this.canDeleteProject;
    },

    headerBadges() {
      const badges = [];
      
      // Add status badge if available
      if (this.project.status) {
        const statusVariant = this.getStatusVariant(this.project.status);
        badges.push({
          text: this.project.status,
          class: statusVariant
        });
      }
      
      // Add stage badge
      if (this.project.stage) {
        badges.push({
          text: this.project.stage,
          class: 'badge-default'
        });
      }
      
      // Add project type badge
      if (this.project.project_type) {
        badges.push({
          text: this.project.project_type,
          class: 'badge-secondary'
        });
      }
      
      return badges;
    },

    manageProjectActions() {
      const actions = [];
      
      if (this.canManageProject) {
        actions.push({
          label: 'Comps',
          action: 'manage-comps',
          icon: 'building'
        });
      }
      
      if (this.canManageProject) {
        actions.push({
          label: 'Sites',
          action: 'manage-sites',
          icon: 'home'
        });
      }
      
      if (this.canDeleteProject) {
        actions.push({
          label: 'Delete Project',
          action: 'delete',
          icon: 'trash',
          danger: true
        });
      }
      
      return actions;
    },

    benchmarkPercentage() {
      return this.project.benchmark || 0;
    },

    benchmarkStatus() {
      if (this.benchmarkPercentage > 0) return 'over';
      if (this.benchmarkPercentage < 0) return 'under';
      return 'at';
    },

    benchmarkClass() {
      return `project-details-benchmark__value--${this.benchmarkStatus}`;
    },

    totalKSDWeight() {
      return this.ksdCriteria.reduce((sum, criterion) => sum + (parseInt(criterion.weight) || 0), 0);
    },

    unallocatedPercentage() {
      return 100 - this.totalKSDWeight;
    },

    keyDatesCount() {
      if (!this.project.sites) return 0;
      
      return this.project.sites.reduce((total, site) => {
        if (site.archived_at) return total;
        
        return total + (site.leases || []).reduce((leaseTotal, lease) => {
          return leaseTotal + (lease.key_dates?.length || 0);
        }, 0);
      }, 0);
    },

    availableTabs() {
      return [
        { slug: 'comps', label: 'Comps', enabled: true, count: this.projectComps?.all?.length || 0 },
        { slug: 'sites', label: 'Sites', enabled: true },
        { slug: 'tasks', label: 'Tasks', enabled: true, count: this.taskCount !== null ? this.taskCount : (this.project.new_tasks?.length || 0) },
        { slug: 'finalist-grid', label: 'Finalist Grid', enabled: true },
        { slug: 'tours', label: 'Tours', enabled: true, count: this.project.tours?.length || 0 },
        { slug: 'proposals', label: 'Proposals', enabled: true, count: this.project.proposals?.length || 0 },
        { slug: 'key-dates', label: 'Key Dates', enabled: true, count: this.keyDatesCount },
        { slug: 'documents', label: 'Documents', enabled: true },
        { slug: 'contacts', label: 'Contacts', enabled: true, count: this.project.contacts?.length || 0 },
        { slug: 'comments', label: 'Comments', enabled: true },
        { slug: 'changelog', label: 'Changelog', enabled: this.$gate.isAdmin() }
      ].filter(tab => tab.enabled);
    },

    otherTabs() {
      return this.availableTabs.filter(t => t.slug !== 'finalist-grid' && t.slug !== 'comps' && t.slug !== 'sites' && t.slug !== 'tasks' && t.slug !== 'tours' && t.slug !== 'proposals' && t.slug !== 'key-dates' && t.slug !== 'contacts' && t.slug !== 'documents');
    },

    showCriteriaComparison() {
      return this.alternativeSites.length > 0 && this.ksdCriteria.length > 0;
    },

    hasKsdCriteria() {
      return this.ksdCriteria.length > 0;
    },

    hasAlternativeSites() {
      return this.alternativeSites.length > 0;
    },

    sitesTableColumns() {
      return [
        { field: 'image', label: 'Image', sortable: false, width: '80px' },
        { field: 'address', label: 'Address', sortable: true },
        { field: 'lease_or_own', label: 'Lease or Own', sortable: true },
        { field: 'due_in', label: 'Due In', sortable: false, align: 'center' },
        { field: 'lease_cost', label: 'Lease Cost', sortable: true, align: 'right' },
        { field: 'remaining', label: 'Remaining', sortable: true, align: 'right' },
        { field: 'current_psf', label: 'Current PSF', sortable: true, align: 'right' },
        { field: 'utilization', label: 'Utilization', sortable: true, align: 'right' }
      ];
    },

    filteredSites() {
      let sites = this.project.sites || [];
      
      // Apply search filter
      if (this.sitesSearchQuery) {
        const query = this.sitesSearchQuery.toLowerCase();
        sites = sites.filter(site => 
          site.building?.address_line_1?.toLowerCase().includes(query) ||
          site.building?.city?.toLowerCase().includes(query) ||
          site.building?.state?.toLowerCase().includes(query)
        );
      }
      
      // Apply sorting
      if (this.sitesSortColumn) {
        sites = [...sites].sort((a, b) => {
          let aVal, bVal;
          
          switch (this.sitesSortColumn) {
            case 'address':
              aVal = a.building?.address_line_1 || '';
              bVal = b.building?.address_line_1 || '';
              break;
            case 'lease_end_date':
              aVal = a.due_in_days || 0;
              bVal = b.due_in_days || 0;
              break;
            case 'utilization':
              aVal = a.utilization || 0;
              bVal = b.utilization || 0;
              break;
            default:
              return 0;
          }
          
          if (typeof aVal === 'string') {
            return this.sitesSortDirection === 'asc' 
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          }
          
          return this.sitesSortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        });
      }
      
      return sites;
    },

    sitesTableData() {
      const sites = this.filteredSites;
      return sites.map(site => ({
        id: site.id,
        image: site.building?.image?.view_url || '',
        address: {
          line1: site.building?.address_line_1 || 'N/A',
          city: site.building?.city || '',
          state: site.building?.state || ''
        },
        lease_or_own: site.lease_or_own || 'N/A',
        due_in: {
          text: site.due_in_text || 'N/A',
          days: site.due_in_days || 0,
          status_color: site.status_color || ''
        },
        lease_cost: site.lease_cost || 0,
        remaining: site.shortened_remaining_lease_cost || 0,
        remaining_full: site.remaining_lease_cost || 0,
        current_psf: site.current_psf || 0,
        utilization: site.utilization || null,
        utilization_color: site.utilization_color || '',
        view_url: site.view_url || `/sites/${site.id}`,
        is_upcoming: site.is_upcoming || false,
        archived_at: site.archived_at || null
      }));
    },

    hasSitesData() {
      return this.sitesTableData.length > 0;
    }
  },
  watch:{
    viewMode(newValue) {
      // Save preference to localStorage if enabled
      if (!this.disablePreferences) {
        localStorage.setItem('sites_view_mode', newValue);
      }
      
      // Announce to screen readers
      this.$nextTick(() => {
        const message = newValue === 'grid' ? 'Switched to grid view' : 'Switched to list view';
        this.announceToScreenReader(message);
      });
    },
  },

  mounted() {
    console.log('ProjectDetails mounted - project data:', this.project);
    console.log('Project comps:', this.project.comps);
    
    // Initialize Vuex stores with project data (same as old Project.vue)
    this.projectComps.all = this.project.comps || [];
    this.projectComps.filtered = this.project.comps || [];
    
    console.log('After setting projectComps:', this.projectComps);
    
    this.projects.selectedProject = this.project;
    
    this.documents.all = this.project.documents || [];
    this.documents.filtered = this.project.documents || [];
    
    // Initialize contacts (same as Site.vue)
    this.contacts.all = this.project.contacts || [];
    this.contacts.filtered = this.project.contacts || [];
    
    this.ksds.scenarios = this.project.ksd_scenarios || [];
    this.ksds.selectedScenarioId = this.ksds.scenarios[0]?.id || null;

    // Initialize KSD scenarios for local component
    this.ksdScenarios = this.project.ksd_scenarios || [];
    this.selectedScenarioId = this.ksdScenarios[0]?.id || null;

    // Load KSD criteria from project data
    if (this.project.ksd_scenarios && this.project.ksd_scenarios.length > 0) {
      const firstScenario = this.project.ksd_scenarios[0];
      if (firstScenario.ksds) {
        this.ksdCriteria = firstScenario.ksds;
      }
    }

    // Load alternative sites from project data (comps for Finalist Grid)
    if (this.project.comps && Array.isArray(this.project.comps)) {
      this.alternativeSites = this.project.comps;
    }

    // Mark the initial tab as loaded so its component mounts immediately
    this.$set(this.tabLoaded, this.activeTab, true);

    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside);

    const savedViewMode = localStorage.getItem('sites_view_mode');
      if (savedViewMode) {
        this.viewMode = savedViewMode;
      }
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
  toggleSortFilter() {
      this.sortFilterOpen = !this.sortFilterOpen;
      this.displayCountOpen = false;
    },
  toggleDisplayCount() {
      this.displayCountOpen = !this.displayCountOpen;
      this.sortFilterOpen = false;
    },

    switchTab(tabSlug) {
      this.activeTab = tabSlug;
      // KAN-173: mark tab as loaded on first activation (enables v-if lazy mount)
      if (!this.tabLoaded[tabSlug]) {
        this.$set(this.tabLoaded, tabSlug, true);
      }
      this.announceTabChange(tabSlug);
    },

    navigateBack() {
      window.location.href = '/projects';
    },

    toggleManageDropdown() {
      this.showManageDropdown = !this.showManageDropdown;
    },

    handleClickOutside(event) {
      const dropdown = this.$el.querySelector('.project-details__manage-dropdown');
      const button = this.$el.querySelector('.project-details__manage-button');
      
      if (dropdown && button && !dropdown.contains(event.target) && !button.contains(event.target)) {
        this.showManageDropdown = false;
      }
    },

    startEditingDetails() {
      // Store original project data for cancel
      this.originalProject = JSON.parse(JSON.stringify(this.project));
      this.isEditingDetails = true;
    },

    cancelEditingDetails() {
      // Restore original project data
      if (this.originalProject) {
        this.project = JSON.parse(JSON.stringify(this.originalProject));
        this.originalProject = null;
      }
      this.isEditingDetails = false;
    },

    async saveProjectDetails() {
      this.isSaving = true;

      try {
        const response = await axios.patch(`/projects/${this.project.id}`, this.project);
        
        // Update project data with response
        this.project = response.data.project;
        this.originalProject = null;
        this.isEditingDetails = false;

        // Update Vuex store if needed
        if (this.projects.selectedProject) {
          this.projects.selectedProject = response.data.project;
        }

        this.$notify({
          type: 'success',
          title: 'Success',
          text: response.data.message || 'Project updated successfully'
        });
      } catch (err) {
        console.error('Error saving project:', err);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Unable to save project'
        });
      } finally {
        this.isSaving = false;
      }
    },

    editProject() {
      // Open edit mode when clicking from manage dropdown
      this.startEditingDetails();
      this.showManageDropdown = false;
    },

    startEditingKsd() {
      // Store original KSD data for cancel
      this.originalKsdCriteria = JSON.parse(JSON.stringify(this.ksdCriteria));
      this.isEditingKsd = true;
    },

    cancelEditingKsd() {
      // Restore original KSD data
      if (this.originalKsdCriteria) {
        this.ksdCriteria = JSON.parse(JSON.stringify(this.originalKsdCriteria));
        this.originalKsdCriteria = null;
      }
      this.isEditingKsd = false;
    },

    addKsdCriterion() {
      // Get the current scenario
      const scenario = this.project.ksd_scenarios && this.project.ksd_scenarios[0];
      
      if (!scenario || !scenario.id) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'No KSD scenario found'
        });
        return;
      }

      // Add new criterion - matching the structure from the old component
      this.ksdCriteria.push({
        scenario_id: scenario.id,
        ksdable_id: this.project.id,
        ksdable_type: 'App\\Project',
        driver: '',
        sort_order: this.ksdCriteria.length,
        weight: 0
      });
    },

    removeKsdCriterion(index) {
      const criterion = this.ksdCriteria[index];
      
      // If it's a new criterion (no ID), just remove it
      if (!criterion.id) {
        this.ksdCriteria.splice(index, 1);
        return;
      }

      // Confirm deletion for existing criteria
      this.$swal({
        title: 'Are you sure?',
        text: "Are you sure you want to delete this KSD criterion?",
        type: 'warning',
        buttonsStyling: false,
        confirmButtonText: 'Yes - Delete it',
        confirmButtonClass: 'btn btn-red',
        showCancelButton: true,
        cancelButtonClass: 'btn btn-secondary mr-4',
        reverseButtons: true
      }).then(async (result) => {
        if (result.value) {
          try {
            await axios.delete(`/ksds/${criterion.id}`);
            
            this.ksdCriteria.splice(index, 1);
            
            this.$notify({
              type: 'success',
              title: 'Success',
              text: 'KSD criterion deleted'
            });
          } catch (err) {
            console.error('Error deleting KSD criterion:', err);
            this.$notify({
              type: 'error',
              title: 'Error',
              text: err.response?.data?.message || 'Unable to delete KSD criterion'
            });
          }
        }
      });
    },

    async saveKsdCriteria() {
      // Validate total weight
      if (this.unallocatedPercentage < 0) {
        this.$swal({
          title: 'Invalid Weights',
          text: "Total weight cannot exceed 100%",
          type: 'warning',
          buttonsStyling: false,
          confirmButtonText: 'OK',
          confirmButtonClass: 'btn btn-green',
        });
        return;
      }

      this.isSavingKsd = true;

      try {
        // Get the scenario from project data
        const scenario = this.project.ksd_scenarios && this.project.ksd_scenarios[0];
        
        if (!scenario || !scenario.id) {
          throw new Error('No KSD scenario found');
        }

        // Update sort orders
        this.ksdCriteria.forEach((ksd, index) => {
          ksd.sort_order = index;
        });

        // Prepare the scenario data - send the entire scenario object like the old component
        const scenarioData = {
          ...scenario,
          ksds: this.ksdCriteria
        };

        const response = await axios.patch(`/ksds/scenarios/${scenario.id}`, scenarioData);
        
        // Update KSD criteria with response data
        this.ksdCriteria = response.data.scenario.ksds;
        this.originalKsdCriteria = null;
        this.isEditingKsd = false;

        // Update the project's ksd_scenarios
        if (this.project.ksd_scenarios && this.project.ksd_scenarios[0]) {
          this.project.ksd_scenarios[0].ksds = response.data.scenario.ksds;
        }

        // Update Vuex store
        if (this.ksds.scenarios && this.ksds.scenarios[0]) {
          this.ksds.scenarios[0].ksds = response.data.scenario.ksds;
        }

        this.$notify({
          type: 'success',
          title: 'Success',
          text: response.data.message || 'KSD criteria updated successfully'
        });
      } catch (err) {
        console.error('Error saving KSD criteria:', err);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Unable to save KSD criteria'
        });
      } finally {
        this.isSavingKsd = false;
      }
    },

    editKSD() {
      // Open edit mode when clicking from other places
      this.startEditingKsd();
    },

    openManageScenarios() {
      this.$modal.show('manage-scenarios');
    },

    handleScenarioChange() {
      // Load KSD criteria for the selected scenario
      const selectedScenario = this.ksdScenarios.find(s => s.id === this.selectedScenarioId);
      if (selectedScenario && selectedScenario.ksds) {
        this.ksdCriteria = selectedScenario.ksds;
      } else {
        this.ksdCriteria = [];
      }
      
      // Update Vuex store
      this.ksds.selectedScenarioId = this.selectedScenarioId;
    },

    handleScenariosUpdated(updatedScenarios) {
      // Update local scenarios
      this.ksdScenarios = updatedScenarios;
      this.project.ksd_scenarios = updatedScenarios;
      
      // Update Vuex store
      this.ksds.scenarios = updatedScenarios;
      
      // If current scenario was deleted, select the first one
      if (!this.ksdScenarios.find(s => s.id === this.selectedScenarioId)) {
        this.selectedScenarioId = this.ksdScenarios[0]?.id || null;
        this.handleScenarioChange();
      }
    },

    deleteProject() {
      this.$swal({
        title: 'Are you sure?',
        text: "Are you sure you want to delete this project?",
        type: 'warning',
        buttonsStyling: false,
        confirmButtonText: 'Yes - Delete it',
        confirmButtonClass: 'btn btn-red',
        showCancelButton: true,
        cancelButtonClass: 'btn btn-secondary mr-4',
        reverseButtons: true
      }).then((result) => {
        if (result.value) {
          this.performDelete();
        }
      });
      this.showManageDropdown = false;
    },

    async performDelete() {
      try {
        await axios.delete(`/projects/${this.project.id}`);
        
        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'Project Deleted'
        });

        setTimeout(() => {
          window.location.href = '/projects';
        }, 1000);
      } catch (err) {
        console.error('Error deleting project:', err);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Unable to delete project'
        });
      }
    },

    getStatusVariant(status) {
      const statusMap = {
        'ACTIVE': 'badge-success',
        'INACTIVE': 'secondary',
        'ARCHIVED': 'destructive'
      };
      return statusMap[status] || 'default';
    },

    formatNumber(value) {
      if (!value && value !== 0) return 'N/A';
      return new Intl.NumberFormat().format(value);
    },

    formatCurrency(value, decimals = 0) {
      if (!value && value !== 0) return '$0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value);
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    getSiteLocation(site) {
      if (!site.building) return 'N/A';
      const parts = [];
      if (site.building.city) parts.push(site.building.city);
      if (site.building.state) parts.push(site.building.state);
      return parts.length > 0 ? parts.join(', ') : 'N/A';
    },

    getCompLocation(comp) {
      const parts = [];
      if (comp.city) parts.push(comp.city);
      if (comp.state) parts.push(comp.state);
      return parts.length > 0 ? parts.join(', ') : 'N/A';
    },

    announceTabChange(tabSlug) {
      const tab = this.availableTabs.find(t => t.slug === tabSlug);
      if (tab) {
        this.announcement = `Switched to ${tab.label} tab`;
        setTimeout(() => {
          this.announcement = '';
        }, 1000);
      }
    },

    onTaskSaved() {
      // Refresh the task count badge from the API after any task save (create/update/delete)
      axios.get(`/api/tasks?project=${this.project.id}&per_page=1`)
        .then(res => {
          this.taskCount = res.data.total ?? res.data.meta?.total ?? null;
        })
        .catch(() => {});
    },

    onTaskDeleted() {
      this.onTaskSaved();
    },

    announceToScreenReader(message) {
      this.announcement = message;
      setTimeout(() => {
        this.announcement = '';
      }, 1000);
    },

    navigateToSite(comp) {
      // For comps in Finalist Grid, use the pivot view_url
      if (comp.pivot && comp.pivot.view_url) {
        window.location.href = comp.pivot.view_url;
      }
    },

    async toggleFavorite(site) {
      if (!site.pivot || !site.pivot.id) {
        console.error('Site pivot data missing');
        return;
      }
      
      const previousState = site.pivot.is_favorite;
      const favorited_at = previousState ? null : moment().format('Y-MM-D H:m:ss');
      const favorited_by = previousState ? null : window.user.id;
      
      // Optimistic update
      site.pivot.favorited_at = favorited_at;
      site.pivot.favorited_by = favorited_by;
      site.pivot.is_favorite = favorited_at ? true : false;
      
      // Get comp name for announcement
      const siteName = site.address_line_1 || 'Comp';
      
      // Announce to screen readers
      this.announcement = site.pivot.is_favorite ? 
        `${siteName} added to favorites` : 
        `${siteName} removed from favorites`;
      
      try {
        // Use pivot ID for the API call - COMPS not sites!
        await axios.patch(`/projects/${this.project.id}/comps/${site.pivot.id}`, {
          favorited_at: favorited_at,
          favorited_by: favorited_by
        });
        
        this.$notify({
          type: 'success',
          title: 'Success',
          text: site.pivot.is_favorite ? 'Added to favorites' : 'Removed from favorites'
        });
      } catch (err) {
        // Revert on error
        site.pivot.is_favorite = previousState;
        site.pivot.favorited_at = previousState ? moment().format('Y-MM-D H:m:ss') : null;
        site.pivot.favorited_by = previousState ? window.user.id : null;
        console.error('Error toggling favorite:', err);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Unable to update favorite status'
        });
      }
      
      setTimeout(() => {
        this.announcement = '';
      }, 1000);
    },

    removeSite(site) {
      if (!site.pivot || !site.pivot.id) {
        console.error('Site pivot data missing');
        return;
      }
      
      const siteName = site.address_line_1 || 'this comp';
      
      this.$swal({
        title: 'Remove Comp?',
        text: `Are you sure you want to remove ${siteName} from consideration?`,
        type: 'warning',
        buttonsStyling: false,
        confirmButtonText: 'Yes - Remove it',
        confirmButtonClass: 'btn btn-red',
        showCancelButton: true,
        cancelButtonClass: 'btn btn-secondary mr-4',
        reverseButtons: true
      }).then(async (result) => {
        if (result.value) {
          try {
            // Use pivot ID for the API call - COMPS not sites!
            await axios.delete(`/projects/${this.project.id}/comps/${site.pivot.id}`);
            
            // Remove from local array
            const index = this.alternativeSites.findIndex(s => s.id === site.id);
            if (index > -1) {
              this.alternativeSites.splice(index, 1);
            }
            
            // Also remove from project.sites if it exists
            if (this.project.sites) {
              const projectIndex = this.project.sites.findIndex(s => s.id === site.id);
              if (projectIndex > -1) {
                this.project.sites.splice(projectIndex, 1);
              }
            }
            
            this.$notify({
              type: 'success',
              title: 'Success',
              text: 'Comp removed from consideration'
            });
          } catch (err) {
            console.error('Error removing comp:', err);
            this.$notify({
              type: 'error',
              title: 'Error',
              text: err.response?.data?.message || 'Unable to remove comp'
            });
          }
        }
      });
    },

    getScoreBadgeClass(score) {
      if (score >= 80) return 'site-card__score-badge--high';
      if (score >= 60) return 'site-card__score-badge--medium';
      return 'site-card__score-badge--low';
    },

    getScore(site, criterion) {
      // Find the score for this site and criterion combination
      if (site.ksd_scores && Array.isArray(site.ksd_scores)) {
        const scoreEntry = site.ksd_scores.find(s => s.ksd_id === criterion.id);
        if (scoreEntry) {
          return scoreEntry.score || 0;
        }
      }
      return 0;
    },

    getCompKSDScore(ksd, compPivotId) {
      // Find the score for this comp in the KSD's scores array
      if (!ksd.scores || !Array.isArray(ksd.scores)) return 0;
      const scoreEntry = ksd.scores.find(s => s.scorable_id === compPivotId);
      return scoreEntry?.score || 0;
    },

    getStarIcon(star, score) {
      return star <= score ? 'star-filled' : 'star-outline';
    },

    getStarLabel(comp, criterion) {
      const score = this.getCompKSDScore(criterion, comp.pivot?.id);
      return `${comp.address_line_1} ${criterion.driver}: ${score} out of 5 stars`;
    },

    handleCompFavoriteToggled({ comp, is_favorite }) {
      // Update the comp in the project.comps array
      const compIndex = this.project.comps.findIndex(c => c.id === comp.id);
      if (compIndex > -1) {
        this.project.comps[compIndex].pivot.is_favorite = is_favorite;
      }
      
      // Also update Vuex store
      if (this.projectComps.all) {
        const storeIndex = this.projectComps.all.findIndex(c => c.id === comp.id);
        if (storeIndex > -1 && this.projectComps.all[storeIndex].pivot) {
          this.projectComps.all[storeIndex].pivot.is_favorite = is_favorite;
        }
      }
    },

    handleCompRemovedFromList(comp) {
      // Remove from local project.comps array
      const compIndex = this.project.comps.findIndex(c => c.id === comp.id);
      if (compIndex > -1) {
        this.project.comps.splice(compIndex, 1);
      }
      
      // Also update Vuex store
      if (this.projectComps.all) {
        const allIndex = this.projectComps.all.findIndex(c => c.id === comp.id);
        if (allIndex > -1) {
          this.projectComps.all.splice(allIndex, 1);
        }
      }
      
      if (this.projectComps.filtered) {
        const filteredIndex = this.projectComps.filtered.findIndex(c => c.id === comp.id);
        if (filteredIndex > -1) {
          this.projectComps.filtered.splice(filteredIndex, 1);
        }
      }
      
      // Update the comps count in the tab
      const compsTab = this.availableTabs.find(t => t.slug === 'comps');
      if (compsTab) {
        compsTab.count = this.project.comps.length;
      }
    },

    handleFinalistCompRemoved(comp) {
      // Remove from alternativeSites array
      const index = this.alternativeSites.findIndex(c => c.id === comp.id);
      if (index > -1) {
        this.alternativeSites.splice(index, 1);
      }
      
      // Also remove from project.comps
      const compIndex = this.project.comps.findIndex(c => c.id === comp.id);
      if (compIndex > -1) {
        this.project.comps.splice(compIndex, 1);
      }
      
      // Update Vuex store
      if (this.projectComps.all) {
        const allIndex = this.projectComps.all.findIndex(c => c.id === comp.id);
        if (allIndex > -1) {
          this.projectComps.all.splice(allIndex, 1);
        }
      }
    },

    handleManageProjectAction(action) {
      switch (action) {
        case 'manage-comps':
          this.openManageCompsModal();
          break;
        case 'manage-sites':
          this.manageSites();
          break;
        case 'delete':
          this.deleteProject();
          break;
        default:
          console.warn('Unknown action:', action);
      }
    },

    manageSites() {
      this.$modal.show('manage-sites-modal');
      this.showManageDropdown = false;
    },

    openManageCompsModal() {
      this.$modal.show('manage-comps-modal');
      this.showManageDropdown = false;
    },

    /**
     * Handles proposal creation
     * Adds the new proposal to the project's proposals array
     * @param {Object} proposal - The newly created proposal object
     */
    handleCompAdded(comps) {
      // Update project comps
      if (this.project) {
        this.project.comps = comps;
        this.project.comps_count = comps.length;
      }
    },

    handleCompRemoved(comps) {
      // Update project comps
      if (this.project) {
        this.project.comps = comps;
        this.project.comps_count = comps.length;
      }
    },

    async handleProposalCreated() {
      // Refresh project data to get the new proposal
      try {
        const response = await axios.get(`/projects/${this.project.id}`);
        if (response.data && response.data.proposals) {
          this.project.proposals = response.data.proposals;
          
          // Update the proposals count in the tab
          const proposalsTab = this.availableTabs.find(t => t.slug === 'proposals');
          if (proposalsTab) {
            proposalsTab.count = this.project.proposals.length;
          }
        }
      } catch (err) {
        console.error('Error refreshing proposals:', err);
      }
    },

    handleSitesUpdated(updatedSites) {
      // Update local project sites data with reactivity
      this.$set(this.project, 'sites', updatedSites);
      
      // Force update the project object to trigger reactivity
      this.project = { ...this.project, sites: updatedSites };
      
      // Announce change for screen readers
      this.announcement = `Sites updated. ${updatedSites.length} site${updatedSites.length !== 1 ? 's' : ''} in project.`;
      
      // Clear announcement after a delay
      setTimeout(() => {
        this.announcement = '';
      }, 3000);
    }
  }
};
</script>
